import Axios, { InternalAxiosRequestConfig } from 'axios';
import { getReasonPhrase } from 'http-status-codes';
import { toast } from 'sonner';

import { API_KEY, API_URL, API_VERSION } from '@/config';

import { errorCodeMapper } from './error-codes';

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  config.headers.Accept = 'application/json';
  return config;
}

export const api = Axios.create({
  baseURL: `${API_URL}/${API_VERSION}`,
  headers: {
    Authorization: API_KEY,
  },
});

const defaultError = {
  code: 'INTERNAL_ERROR',
  message:
    'Something went wrong. Please check your internet connection or contact our support.',
  status: 503,
  data: {},
};

api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (Axios.isCancel(error)) {
      // Do not show a toaster for canceled requests
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      const errorResponse = data.error || {};
      const customError = errorCodeMapper[errorResponse.code];

      const errorTitle =
        customError?.title || getReasonPhrase(status) || 'Error';
      let errorMessage =
        customError?.message || errorResponse.message || defaultError.message;

      if (
        errorResponse.code === 'BAD_USER_INPUT' &&
        errorResponse.data?.fields &&
        Object.keys(errorResponse.data.fields).length === 1
      ) {
        // Extract the single validation message
        errorMessage = Object.values(errorResponse.data.fields)[0] as string;
      }

      if (errorResponse.data?.retryable) {
        return Promise.reject(data.error); // Silent during retries
      }

      toast.error(errorTitle, {
        description: errorMessage,
      });
      return Promise.reject(data.error);
    } else {
      toast.error('Error', {
        id: 'unexpected-error',
        description: 'An unexpected error occurred. Please try again later.',
      });

      return Promise.reject(error);
    }
  },
);
